import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, DatePicker, message, Select, Descriptions, InputNumber, Typography } from 'antd';
import { INVOICE_API } from '../../constants/api';
import axios from '../../services/axiosConfig';
import dayjs from 'dayjs';

const { Text } = Typography;

const PaymentModal = ({ visible, record, onCancel, onSuccess }) => {
  const [form] = Form.useForm();
  const [paymentMethod, setPaymentMethod] = useState([]);
  const [invoiceDetail, setInvoiceDetail] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchInvoiceDetail = async () => {
    if (!record || !record.id) return;
    
    try {
      setLoading(true);
      const response = await axios.get(INVOICE_API.DETAIL(record.id));
      const detail = response.data.data;
      setInvoiceDetail(detail);
      
      const remainingAmount = (detail.Amount || 0) - (detail.PaidAmount || 0);
      form.setFieldsValue({
        amount: remainingAmount > 0 ? remainingAmount : 0,
        paymentDate: dayjs(),
        paymentMethod: "Chuyển khoản ngân hàng"
      });
    } catch (error) {
      console.error('Lỗi khi tải chi tiết hóa đơn:', error);
      message.error('Không thể tải chi tiết hóa đơn');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (visible && record) {
      fetchInvoiceDetail();
    } else {
      form.resetFields();
      setInvoiceDetail(null);
    }
  }, [visible, record, form]);

  const handleSubmit = async (values) => {
    try {
      if (!invoiceDetail) {
        message.error('Không có thông tin hóa đơn');
        return;
      }
      
      const remainingAmount = (invoiceDetail.Amount || 0) - (invoiceDetail.PaidAmount || 0);
      if (values.amount > remainingAmount) {
        message.error(`Số tiền thanh toán không được vượt quá số tiền còn thiếu: ${new Intl.NumberFormat('vi-VN').format(remainingAmount)} VND`);
        return;
      }

      await axios.post(INVOICE_API.PAY(record.id), {
        amount: values.amount,
        paymentDate: values.paymentDate.format('YYYY-MM-DD'),
        paymentMethod: values.paymentMethod,
        note: values.note || ''
      });
      message.success('Ghi nhận thanh toán thành công');
      form.resetFields();
      onSuccess();
    } catch (error) {
      console.error('Lỗi khi thanh toán:', error);
      message.error('Không thể ghi nhận thanh toán');
    }
  };

  useEffect(() => {
    setPaymentMethod([
      { value: "Chuyển khoản ngân hàng", label: "Chuyển khoản ngân hàng" },
      { value: "Tiền mặt", label: "Tiền mặt" }
    ]);
  }, []);

  return (
    <Modal
      title="Ghi nhận thanh toán hóa đơn tổng hợp"
      open={visible}
      onCancel={onCancel}
      onOk={() => form.submit()}
      width={600}
      confirmLoading={loading}
    >
      {invoiceDetail && (
        <Descriptions bordered column={1} size="small" style={{ marginBottom: 24 }}>
          <Descriptions.Item label="Mã hóa đơn">
            <Text strong>{invoiceDetail.InvoiceID || record.id}</Text>
          </Descriptions.Item>
          <Descriptions.Item label="Khách thuê">
            {invoiceDetail.TenantName || record.tenantName}
          </Descriptions.Item>
          <Descriptions.Item label="Kỳ thanh toán">
            {invoiceDetail.Period || record.period}
          </Descriptions.Item>
          <Descriptions.Item label="Số tiền cần thanh toán">
            <Text strong style={{ color: '#1890ff' }}>
              {new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(invoiceDetail.Amount)}
            </Text>
          </Descriptions.Item>
          <Descriptions.Item label="Đã thanh toán">
            <Text type="success">
              {new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(invoiceDetail.PaidAmount || 0)}
            </Text>
          </Descriptions.Item>
          <Descriptions.Item label="Còn thiếu">
            <Text type="danger" strong>
              {new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format((invoiceDetail.Amount || 0) - (invoiceDetail.PaidAmount || 0))}
            </Text>
          </Descriptions.Item>
        </Descriptions>
      )}

      <Form form={form} onFinish={handleSubmit} layout="vertical">
        <Form.Item 
          name="amount" 
          label="Số tiền thanh toán" 
          rules={[
            { required: true, message: 'Vui lòng nhập số tiền' },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (value === undefined || value === null || value <= 0) {
                  return Promise.reject(new Error('Số tiền phải lớn hơn 0'));
                }
                if (invoiceDetail) {
                  const remaining = (invoiceDetail.Amount || 0) - (invoiceDetail.PaidAmount || 0);
                  if (value > remaining) {
                    return Promise.reject(new Error('Số tiền không được vượt quá số tiền còn thiếu'));
                  }
                }
                return Promise.resolve();
              },
            }),
          ]}
        >
          <InputNumber
            style={{ width: '100%' }}
            min={0}
            formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
            parser={value => value.replace(/\$\s?|(,*)/g, '')}
            placeholder="Nhập số tiền"
          />
        </Form.Item>
        <Form.Item name="paymentDate" label="Ngày thanh toán" rules={[{ required: true, message: 'Vui lòng chọn ngày thanh toán' }]}>
          <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
        </Form.Item>
        <Form.Item name="paymentMethod" label="Phương thức thanh toán" rules={[{ required: true, message: 'Vui lòng chọn phương thức' }]}>
          <Select
            placeholder="Chọn phương thức thanh toán"
            options={paymentMethod}
          />
        </Form.Item>
        <Form.Item name="note" label="Ghi chú">
          <Input.TextArea rows={3} placeholder="Nhập ghi chú thanh toán (nếu có)" />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default PaymentModal;